import dayjs from "dayjs";
import { Shipment } from "../../services/prePlanningService.types";
import { BaseQueryParams } from "../../types/filter";
import {
  AppointmentDateQueryParam,
  filterAppointmentDate,
  getAppointmentDateFilter,
} from "./commonFilters/appointmentDateFilter";
import { BillsQueryParam } from "./commonFilters/billsFilter";
import {
  ConsigneeAddressQueryParam,
  filterConsigneeAddress,
  getDeliveryAddressFilter,
} from "./commonFilters/consigneeAddressFilter";
import {
  ConsigneeCityQueryParam,
  filterConsigneeCity,
  getDeliveryCityFilter,
} from "./commonFilters/consigneeCityFilter";
import {
  ConsigneeNameQueryParam,
  filterConsigneeName,
  getConsigneeNameFilter,
} from "./commonFilters/consigneeNameFilter";
import {
  ConsigneeZipQueryParam,
  filterConsigneeZip,
  getDeliveryZipFilter,
} from "./commonFilters/consigneeZipFilter";
import {
  DestinationSICQueryParam,
  filterDestinationSIC,
  getDestinationSicFilter,
} from "./commonFilters/destinationSICFilter";
import { ETAQueryParam, getETAFilter } from "./commonFilters/ETAFilter";
import {
  filterHandlingUnits,
  getHandlingUnitsFilter,
} from "./commonFilters/handlingUnitsFilter";
import { filterPallets, getPalletsFilter } from "./commonFilters/palletsFilter";
import { filterPieces, getPiecesFilter } from "./commonFilters/piecesFilter";
import {
  filterPresetRouteName,
  getPresetRouteNameFilter,
  PresetRouteNameQueryParam,
} from "./commonFilters/presetRouteFilter";
import {
  filterServiceDue,
  getServiceDueFilter,
  ServiceDueDateQueryParam,
} from "./commonFilters/serviceDueDateFilter";
import {
  filterShipper,
  getShipperFilter,
  ShipperNameQueryParam,
} from "./commonFilters/shipperNameFilter";
import {
  filterStatus,
  getStatusFilter,
  StatusQueryParam,
} from "./commonFilters/statusFilter";
import {
  filterTags,
  getTagsFilter,
  TagsQueryParam,
} from "./commonFilters/tagsFilter";
import { filterWeight, getWeightFilter } from "./commonFilters/weightFilter";
import { SummaryQueryParams } from "./summaryFilters";
import { filterDataByInboundFilterURLParams, type Prefix } from "./utils";

type ShipmentQueryParams =
  | Exclude<SummaryQueryParams, BillsQueryParam>
  | StatusQueryParam
  | TagsQueryParam
  | ShipperNameQueryParam
  | ConsigneeNameQueryParam
  | ConsigneeAddressQueryParam
  | ConsigneeCityQueryParam
  | ConsigneeZipQueryParam
  | DestinationSICQueryParam
  | AppointmentDateQueryParam
  | ServiceDueDateQueryParam
  | ETAQueryParam
  | PresetRouteNameQueryParam;

type ShipmentFilters = Record<ShipmentQueryParams, string[] | undefined> & {
  isPlannedFilter?: string[];
};

function filterByEtaBefore(
  shipments: Shipment[],
  timeZone?: string,
  eta?: string[]
): Shipment[] {
  if (!eta || !eta[0] || eta[0] === "All") return shipments;
  const filterDate = dayjs(eta[0]);

  if (!filterDate.isValid()) {
    throw new Error("Invalid ETA filter provided");
  }

  return shipments.filter((item) => {
    const etaString = item.etaDate;

    if (!etaString) {
      return false;
    }

    const itemEtaDate = dayjs(etaString).tz(timeZone);

    if (!itemEtaDate.isValid()) {
      return false;
    }

    return itemEtaDate.isBefore(filterDate);
  });
}

const filterPlanningStatus = (
  data: Shipment[],
  planningStatus: string
): Shipment[] => {
  if (planningStatus === "All") {
    return data;
  }
  return planningStatus === "Planned"
    ? data.filter((entry) => entry.isPlanned)
    : data.filter((entry) => !entry.isPlanned);
};

export const filterShipmentData = (
  shipments: Shipment[],
  {
    presetRouteName,
    serviceDueDate,
    appointmentDate,
    shipperName,
    consigneeName,
    consigneeAddress,
    consigneeCity,
    consigneeZip,
    destinationSicCode,
    status,
    tags,
    pieces,
    handlingUnits,
    pallets,
    weight,
    eta,
    isPlannedFilter,
  }: ShipmentFilters,
  timeZone: string
) => {
  const planningStatus = isPlannedFilter?.[0] ?? "All";
  let result = filterPresetRouteName(shipments, presetRouteName);
  result = filterByEtaBefore(result, timeZone, eta);
  result = filterServiceDue(result, serviceDueDate);
  result = filterShipper(result, shipperName);
  result = filterConsigneeName(result, consigneeName);
  result = filterConsigneeAddress(result, consigneeAddress);
  result = filterConsigneeCity(result, consigneeCity);
  result = filterConsigneeZip(result, consigneeZip);
  result = filterDestinationSIC(result, destinationSicCode);
  result = filterAppointmentDate(result, timeZone, appointmentDate);
  result = filterPieces(result, pieces);
  result = filterHandlingUnits(result, handlingUnits);
  result = filterPallets(result, pallets);
  result = filterWeight(result, weight);
  result = filterTags(result, tags);
  result = filterStatus(result, status);
  result = filterPlanningStatus(result, planningStatus);
  return result;
};

export const getShipmentTableFilters = (
  shipments: Shipment[],
  timeZone: string,
  searchParams: URLSearchParams,
  prefix?: Prefix
) => {
  const getShipments = (param: BaseQueryParams) =>
    filterDataByInboundFilterURLParams(
      searchParams,
      param,
      shipments,
      timeZone,
      prefix
    );

  return [
    getPresetRouteNameFilter(
      getShipments("presetRouteName"),
      "Preset Route Name",
      prefix
    ),
    getServiceDueFilter(getShipments("serviceDueDate"), "UTC", prefix),
    getShipperFilter(getShipments("shipperName"), prefix),
    getConsigneeNameFilter(getShipments("consigneeName"), prefix),
    getDeliveryAddressFilter(getShipments("consigneeAddress"), prefix),
    getDeliveryCityFilter(getShipments("consigneeCity"), prefix),
    getDeliveryZipFilter(getShipments("consigneeZip"), prefix),
    getDestinationSicFilter(getShipments("destinationSicCode"), prefix),
    getAppointmentDateFilter(getShipments("appointmentDate"), timeZone, prefix),
    getPalletsFilter(getShipments("pallets"), prefix),
    getPiecesFilter(getShipments("pieces"), prefix),
    getHandlingUnitsFilter(getShipments("handlingUnits"), prefix),
    getWeightFilter(getShipments("weight"), prefix),
    getTagsFilter(getShipments("tags"), prefix),
    getStatusFilter(getShipments("status"), prefix),
    getETAFilter(shipments, timeZone, prefix),
  ];
};
